import { useContext, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { ToastsContext } from '../core/contexts/ToastsContext'

import useSound from './useSound'

/**
 * Hook to easily use notifications with sound and i18n support
 * Combines ToastsContext, useSound, and i18n for a unified API
 *
 * Supports both plain text and translation keys:
 * - Translation key: notify('toasts:success.saved') - will be translated
 * - Plain text: notify('Operation successful') - backward compatible
 *
 * @returns {Object} Notification methods
 */
const useNotification = () => {
  const toastsContext = useContext(ToastsContext)
  const sound = useSound()
  const { t } = useTranslation('toasts')

  /**
   * Translate message if it looks like a translation key.
   * Keys are compact (no spaces), e.g. "zoning.cannotDeleteBoundaryLocked" or "toasts:success.saved".
   * Already-translated sentences (e.g. "Cannot delete zone: it is associated with...") must not be treated as keys.
   *
   * @param {string} message - Plain text or translation key
   * @param {object} i18nParams - Parameters for interpolation
   * @returns {string} Translated or original message
   */
  const translateMessage = useCallback(
    (message, i18nParams = {}) => {
      if (typeof message !== 'string') return message
      // If message contains a space, it is already plain text / translated sentence, not a key
      if (message.includes(' ')) return message
      // Key-like: contains '.' or ':' and no spaces
      if (message.includes('.') || message.includes(':')) {
        try {
          return t(message, i18nParams)
        } catch (error) {
          console.warn('Translation failed for key:', message, error)
          return message
        }
      }
      return message
    },
    [t]
  )

  // If ToastsContext is available, use it; otherwise just use sound
  const notify = useCallback(
    (message, type = 'info', options = {}) => {
      const { soundOnly = false, visualOnly = false, i18nParams = {} } = options

      // Translate message if needed
      const translatedMessage = translateMessage(message, i18nParams)

      // Play sound unless visualOnly is true
      if (!visualOnly && sound.playSound) {
        sound.playSound(type)
      }

      // Show toast unless soundOnly is true
      if (!soundOnly && toastsContext?.addToast) {
        toastsContext.addToast(translatedMessage, type, { i18nParams })
      }
    },
    [toastsContext, sound, translateMessage]
  )

  /**
   * Show success notification
   * @param {string} message - Plain text or translation key (e.g., 'success.saved')
   * @param {object} options - { i18nParams, soundOnly, visualOnly }
   */
  const success = useCallback(
    (message, options = {}) => {
      const { i18nParams = {} } = options
      const translatedMessage = translateMessage(message, i18nParams)

      if (toastsContext?.addSuccessToast && !options?.soundOnly) {
        toastsContext.addSuccessToast(translatedMessage, options)
      } else {
        notify(message, 'success', options)
      }
    },
    [toastsContext, notify, translateMessage]
  )

  /**
   * Show info notification
   * @param {string} message - Plain text or translation key (e.g., 'info.loading')
   * @param {object} options - { i18nParams, soundOnly, visualOnly }
   */
  const info = useCallback(
    (message, options = {}) => {
      const { i18nParams = {} } = options
      const translatedMessage = translateMessage(message, i18nParams)

      if (toastsContext?.addInfoToast && !options?.soundOnly) {
        toastsContext.addInfoToast(translatedMessage, options)
      } else {
        notify(message, 'info', options)
      }
    },
    [toastsContext, notify, translateMessage]
  )

  /**
   * Show warning notification
   * @param {string} message - Plain text or translation key (e.g., 'warning.unsavedChanges')
   * @param {object} options - { i18nParams, soundOnly, visualOnly }
   */
  const warning = useCallback(
    (message, options = {}) => {
      const { i18nParams = {} } = options
      const translatedMessage = translateMessage(message, i18nParams)

      if (toastsContext?.addWarningToast && !options?.soundOnly) {
        toastsContext.addWarningToast(translatedMessage, options)
      } else {
        notify(message, 'warning', options)
      }
    },
    [toastsContext, notify, translateMessage]
  )

  /**
   * Show error notification
   * @param {string} message - Plain text or translation key (e.g., 'error.generic')
   * @param {object} options - { i18nParams, soundOnly, visualOnly }
   */
  const error = useCallback(
    (message, options = {}) => {
      const { i18nParams = {} } = options
      const translatedMessage = translateMessage(message, i18nParams)

      if (toastsContext?.addErrorToast && !options?.soundOnly) {
        toastsContext.addErrorToast(translatedMessage, options)
      } else {
        notify(message, 'error', options)
      }
    },
    [toastsContext, notify, translateMessage]
  )

  /**
   * Show completion notification
   * @param {string} message - Plain text or translation key (e.g., 'completion.processingComplete')
   * @param {object} options - { i18nParams, soundOnly, visualOnly }
   */
  const completion = useCallback(
    (message, options = {}) => {
      const { i18nParams = {} } = options
      const translatedMessage = translateMessage(message, i18nParams)

      if (toastsContext?.addCompletionToast && !options?.soundOnly) {
        toastsContext.addCompletionToast(translatedMessage, options)
      } else {
        notify(message, 'completion', options)
      }
    },
    [toastsContext, notify, translateMessage]
  )

  return {
    notify,
    success,
    info,
    warning,
    error,
    completion,
    // Direct access to sound controls
    setSoundEnabled: sound.setSoundEnabled,
    setVolume: sound.setVolume,
    isSoundEnabled: sound.isSoundEnabled,
    // Direct access to sound-only methods
    playSuccess: sound.playSuccess,
    playInfo: sound.playInfo,
    playWarning: sound.playWarning,
    playError: sound.playError,
    playCompletion: sound.playCompletion,
  }
}

export default useNotification
